import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  BarController,
  CategoryScale,
  PolarAreaController,
  RadarController,
  RadialLinearScale,
} from "chart.js";
import BarChart, {
  ActivitiesChart,
  BenefsChart,
  ExtendedActivitiesChart,
  PhysicalExecutionChart,
  RiskChart,
  TEFChart,
} from "./graph_components/BarChart";
import { getFromApiFunction } from "../../services/api/apiFunctions";

ChartJS.register(
  LinearScale,
  RadialLinearScale,
  CategoryScale,
  BarElement,
  ArcElement,
  PointElement,
  BarController,
  LineElement,
  RadarController,
  PolarAreaController,
  Filler,
  Tooltip,
  Legend,
);

const scrollBarClasses =
  // "scrollbar-thin scrollbar-thumb-slate-500 scrollbar-track-slate-50 overflow-auto";
  "scrollbar scrollbar-thumb-w-[2px] scrollbar-thumb-rounded-full scrollbar-thumb-gray-400 scrollbar-track-gray-100 flex-grow sm:overflow-auto";

const width = "relative w-[250px] sm:w-fit mb-8 m-auto 2xl:mx-[10px]";

const colors = [
  { barColor: "rgba(255, 99, 132, 0.5)", labelColor: "black" },
  { barColor: "rgba(54, 162, 235, 0.5)", labelColor: "white" },
  { barColor: "rgba(255, 206, 86, 0.5)", labelColor: "black" },
  { barColor: "rgba(75, 192, 192, 0.5)", labelColor: "black" },
  { barColor: "rgba(153, 102, 255, 0.5)", labelColor: "white" },
  { barColor: "rgba(255, 159, 64, 0.5)", labelColor: "black" },
  { barColor: "rgba(255, 99, 132, 0.5)", labelColor: "black" },
  { barColor: "rgba(8, 120, 212, 0.5)", labelColor: "white" },
  { barColor: "rgba(211, 155, 14, 0.5)", labelColor: "black" },
  { barColor: "rgba(29, 73, 73, 0.5)", labelColor: "white" },
];

export default function ChartRenderer(props: any) {
  /**
   * In the following base code:
   * - divisionType is a specific type of territory division (departments or poles)
   * - division is a specific division of a given type (Ex: Alibori for departments and PDA1 for poles)
   */

  const {
    params,
    statsUnitProps,
    buildParameter,
    intervention,
    baseEndpoint,
    otherDataSet,
    stacked,
    indicatorCategory,
    withoutRegionSelector,
    withLegend,
    timelineProps,
  } = props;

  const [value, setValue] = React.useState(0);

  const [indics, setIndics]: any = React.useState([]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [statsUnit, setStatsUnit] = useState(statsUnitProps);
  const [timeline, setTimeline] = useState(timelineProps);

  useEffect(() => {
    setTimeline(timelineProps);
  }, [timelineProps]);

  useEffect(() => {
    if (!intervention.id) {
      return;
    }
    let params = new URLSearchParams([
      ["composante", intervention.id],
      ["category", indicatorCategory],
    ]);
    getFromApiFunction("indicators/", params).then((response) => {
      setIndics(response.data.results);
    });
  }, [intervention]);

  useEffect(() => {
    setStatsUnit(statsUnitProps);
  }, [statsUnitProps]);

  useEffect(() => {}, [statsUnit]);

  return (
    <div className={`${scrollBarClasses} h-full align-center`}>
      <h1 className="text-center font-bold"> TABLEAU DE BORD DE SUIVI </h1>
      <h1 className="text-center font-bold mb-8 sm:w-1/2 m-auto">
        {intervention && intervention.name}
      </h1>
      <div className="gap-4 w-fit m-auto rounded-md flex">
        <div className={width}>
          {indics &&
            indics.map((indic: any, index: any) => {
              let filterParams = new URLSearchParams(params);
              filterParams.delete("composante");
              if (!filterParams.get("indicator")) {
                filterParams.append("indicator__code", indic.code);
              }
              return (
                <BarChart
                  graphName={indic.name_lines}
                  baseEndpoint={baseEndpoint}
                  statsUnitProps={statsUnit}
                  paramsProps={filterParams}
                  buildParameter={buildParameter}
                  // Get a random color for each indic
                  color={colors[index % colors.length].barColor}
                  labelColor={colors[index % colors.length].labelColor}
                  key={index}
                  otherDataSet={otherDataSet}
                  stacked={indic.display_stacked && stacked}
                  withLegend={withLegend}
                  withoutRegionSelector={withoutRegionSelector}
                  customHeight={450}
                  customWidth={700}
                  targetValue={indic.target_value}
                  extra={indic.extra.chart_config}
                  timelineProps={timeline}
                  className="mb-12"
                />
              );
            })}
        </div>
      </div>
    </div>
  );
}

export function SynopticPage() {
  return (
    <div className="bg-white pt-[77px]">
      <div className="w-[90%] m-auto grid grid-cols-3 gap-2 px-4 bg-white auto-rows-[350px]">
        <div className="border-solid border-2 rounded bg-[whitesmoke] w-full">
          <PhysicalExecutionChart titleFontSize={15} className="m-auto" />
        </div>
        <div className="border-solid border-2 rounded bg-[whitesmoke] w-full">
          <TEFChart titleFontSize={14} className="m-auto w-full" height={650} />
        </div>
        <div className="border-solid border-2 rounded bg-[whitesmoke] w-full">
          <BenefsChart />
        </div>
      </div>
      <div className="w-[90%] m-auto grid grid-cols-3 gap-2 px-4 mt-2 bg-white">
        <div className="border-solid border-2 rounded bg-[whitesmoke] w-full col-span-2 py-2">
          <ExtendedActivitiesChart />
          <div className="text-[15px] font-bold w-full text-center mt-4 font-[Arial] text-[#666666]">
            Nombre d'activités
          </div>
        </div>
        <div className="border-solid border-2 rounded bg-[whitesmoke] w-full">
          <RiskChart />
        </div>
      </div>
    </div>
  );
}
